// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pure rules of the corner assistant's queue: which suggestion goes up next,
// how long a held one may wait for the person to be free, when a closed one's dot goes
// away, and what the person did in the five minutes after ignoring one. No stores here,
// so the rules are unit tested (CornerQueue.test.ts). See docs/konvo/assistant-engagement.md.

import type {CornerKind, CornerSuggestion, Starter} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';

/** How long a due suggestion waits for the person to be free before it is dropped. */
export const HOLD_LIMIT_MS = 10 * 60_000;
/** A pop-up nobody touches closes itself after this long, leaving a dot. */
export const AUTO_CLOSE_MS = 20_000;
/** On screen this long counts as seen. */
export const SEEN_AFTER_MS = 1_000;
/** What happened next is judged over this window after a dismissal or time-out. */
export const FOLLOW_WINDOW_MS = 5 * 60_000;
/** Pending suggestions are fetched this often (plus on focus and gateway pushes). */
export const POLL_MS = 2 * 60_000;
/** Typing counts as busy for this long after the last keystroke. */
export const TYPING_IDLE_MS = 8_000;

export type CornerBusyReason = 'in_call' | 'typing' | 'screen_share' | 'hidden_tab';

export interface CornerTimings {
	autoCloseMs: number;
	followMs: number;
	seenMs: number;
	pollMs: number;
	holdMs: number;
}

export const DEFAULT_TIMINGS: CornerTimings = {
	autoCloseMs: AUTO_CLOSE_MS,
	followMs: FOLLOW_WINDOW_MS,
	seenMs: SEEN_AFTER_MS,
	pollMs: POLL_MS,
	holdMs: HOLD_LIMIT_MS,
};

/**
 * Timings with a test override (the sandbox e2e shortens the 20 s auto-close through
 * `?ls.konvo_corner_timings={"autoCloseMs":1500}`). Unknown or bad values fall back.
 */
export function readTimings(raw: string | null | undefined): CornerTimings {
	if (!raw) return DEFAULT_TIMINGS;
	try {
		const parsed = JSON.parse(raw) as Partial<Record<keyof CornerTimings, unknown>>;
		const pick = (key: keyof CornerTimings): number => {
			const value = parsed[key];
			return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : DEFAULT_TIMINGS[key];
		};
		return {
			autoCloseMs: pick('autoCloseMs'),
			followMs: pick('followMs'),
			seenMs: pick('seenMs'),
			pollMs: pick('pollMs'),
			holdMs: pick('holdMs'),
		};
	} catch {
		return DEFAULT_TIMINGS;
	}
}

function ms(iso: string | undefined): number {
	if (!iso) return Number.NaN;
	return Date.parse(iso);
}

/** Past its expiry: never show it (and stop showing its dot). */
export function isExpired(suggestion: Pick<CornerSuggestion, 'expires_at'>, now: number): boolean {
	const expires = ms(suggestion.expires_at);
	return Number.isFinite(expires) && now >= expires;
}

/**
 * Still waiting to be shown: planned, held for a busy moment, or brought back by Later. A
 * 'shown' one is up somewhere already (the server echoes outcomes to every tab and device).
 */
export function isWaiting(status: CornerSuggestion['status']): boolean {
	return status === 'planned' || status === 'held' || status === 'later';
}

/** Due now (or already), not expired, and still waiting to be shown. */
export function isShowable(suggestion: CornerSuggestion, now: number): boolean {
	if (isExpired(suggestion, now)) return false;
	if (!isWaiting(suggestion.status)) return false;
	const due = ms(suggestion.due_at);
	return !Number.isFinite(due) || due <= now;
}

/**
 * Identifies one appearance of a suggestion: "Later" brings the same id back with a new due
 * time, which must be shown again even though the earlier appearance was dealt with.
 */
export function appearanceKey(suggestion: Pick<CornerSuggestion, 'id' | 'due_at'>): string {
	return `${suggestion.id}@${suggestion.due_at}`;
}

/**
 * The one suggestion to put up: meeting prep first (the meeting that starts soonest), then
 * the oldest due. `skip` holds appearance keys this client already dealt with (closed,
 * dropped, used), see appearanceKey.
 */
export function pickNext(
	suggestions: ReadonlyArray<CornerSuggestion>,
	now: number,
	skip: ReadonlySet<string> = new Set(),
): CornerSuggestion | null {
	const ready = suggestions.filter((s) => !skip.has(appearanceKey(s)) && isShowable(s, now));
	if (ready.length === 0) return null;
	const sortKey = (s: CornerSuggestion): number => {
		if (s.kind === 'meeting_prep') {
			const start = ms(s.meeting?.start);
			if (Number.isFinite(start)) return start;
		}
		const due = ms(s.due_at);
		return Number.isFinite(due) ? due : 0;
	};
	return (
		[...ready].sort((a, b) => {
			const prepA = a.kind === 'meeting_prep' ? 0 : 1;
			const prepB = b.kind === 'meeting_prep' ? 0 : 1;
			if (prepA !== prepB) return prepA - prepB;
			const diff = sortKey(a) - sortKey(b);
			return diff !== 0 ? diff : a.id.localeCompare(b.id);
		})[0] ?? null
	);
}

export interface HoldState {
	/** When this client first wanted to show it but the person was busy. */
	since: number;
	/** The 'held' outcome was reported (once). */
	reported: boolean;
}

export type HoldDecision =
	| {action: 'show'}
	| {action: 'hold'; report: boolean; reason: CornerBusyReason}
	| {action: 'drop'};

/**
 * Hold-until-free: show when free; while busy, report 'held' once and keep waiting; after
 * the hold limit (or past its expiry) drop it.
 */
export function decideHold(
	suggestion: CornerSuggestion,
	busy: CornerBusyReason | null,
	hold: HoldState | undefined,
	now: number,
	holdLimitMs = HOLD_LIMIT_MS,
): HoldDecision {
	if (isExpired(suggestion, now)) return {action: 'drop'};
	if (hold && now - hold.since >= holdLimitMs) return {action: 'drop'};
	if (!busy) return {action: 'show'};
	return {action: 'hold', report: !hold?.reported, reason: busy};
}

/** A closed-by-itself suggestion keeps a dot on the icon until it is out of date. */
export function dotVisible(dot: {expiresAt: number} | null, now: number): boolean {
	return dot != null && now < dot.expiresAt;
}

export type FollowedNext = 'same_opened' | 'asked_konvo' | 'left' | 'stayed';

export interface FollowSignals {
	/** Starters the Konvo panel was opened with during the window. */
	panelStarters: ReadonlyArray<Starter>;
	/** Ask / the panel was opened for anything (⌘K Ask row, the corner icon…). */
	askedKonvo: boolean;
	/** Areas (mail, calendar, tasks…) the person went to during the window. */
	areas: ReadonlyArray<string>;
	/** Of the window, how long the tab was hidden or the window blurred. */
	awayMs: number;
	windowMs: number;
}

/** Where "the same thing" lives for each kind. */
const SAME_AREA: Record<CornerKind, string | null> = {
	meeting_prep: 'calendar',
	client_followup: 'mail',
	outstanding_work: 'tasks',
	review_time: 'tasks',
	promise_due: null,
	connect_invite: null,
	agent_task: 'agents',
	waiting_on_you: 'mail',
	waiting_on_them: 'mail',
	wrap_up: 'chats',
};

/**
 * What the person did in the five minutes after dismissing or ignoring a suggestion:
 * opened the same thing on their own (the panel with the same starter, the meeting in
 * Calendar, the sender's mail), asked Konvo something else, mostly left, or stayed.
 */
export function classifyFollowed(kind: CornerKind, starter: Starter | undefined, signals: FollowSignals): FollowedNext {
	if (starter && starter !== 'free' && signals.panelStarters.includes(starter)) return 'same_opened';
	const area = SAME_AREA[kind];
	if (area && signals.areas.includes(area)) return 'same_opened';
	if (signals.askedKonvo || signals.panelStarters.length > 0) return 'asked_konvo';
	if (signals.windowMs > 0 && signals.awayMs / signals.windowMs > 0.5) return 'left';
	return 'stayed';
}

/** The area a path belongs to, for the `page` of outcomes and the follow-up check. */
export function pageForPath(pathname: string): string {
	if (pathname.startsWith('/today') || pathname === '/' || pathname.startsWith('/home')) return 'home';
	if (pathname.startsWith('/emails')) return 'mail';
	if (pathname.startsWith('/calendar')) return 'calendar';
	if (pathname.startsWith('/tasks')) return 'tasks';
	if (pathname.includes('/whatsapp')) return 'whatsapp';
	if (pathname.startsWith('/channels')) return 'chats';
	if (pathname.startsWith('/notes') || pathname.startsWith('/scratchpad')) return 'notes';
	if (pathname.startsWith('/insights')) return 'insights';
	if (pathname.startsWith('/agents')) return 'agents';
	return 'other';
}

/** What decides whether a pop-up can be drawn right now (see Corner.canDraw). */
export interface DrawConditions {
	/** Phone layout: the card rises above the bottom bar's Ask button. */
	phone: boolean;
	/** The Konvo panel is open (the card would sit under it). */
	panelOpen: boolean;
	/** A modal (Settings, the ⌘K palette…) covers the page. */
	modalOpen: boolean;
	/** Desktop: the Konvo (Assistant) DM is open, where the corner hides. */
	inKonvoDm: boolean;
	/** Phone: the bottom bar with its Ask button is on screen. */
	phoneBar: boolean;
	/** Phone: the bar has grown into its Ask or More panel. */
	railPanelOpen: boolean;
	/** Phone: the software keyboard is up (the bar is hidden). */
	keyboardOpen: boolean;
}

/**
 * Whether a pop-up can be seen right now. When it cannot, nothing is shown or reported: a
 * waiting suggestion stays queued, and one already up keeps its place with the clock paused.
 */
export function canDrawCard(conditions: DrawConditions): boolean {
	if (conditions.panelOpen || conditions.modalOpen) return false;
	if (conditions.phone) return conditions.phoneBar && !conditions.railPanelOpen && !conditions.keyboardOpen;
	return !conditions.inKonvoDm;
}

/**
 * An outcome that ends a suggestion for good (the server ignores anything after it): used, or
 * dismissed other than Snooze (which brings it back in an hour).
 */
export function isFinalOutcome(outcome: {status: string; menu?: string}): boolean {
	if (outcome.status === 'used') return true;
	return outcome.status === 'dismissed' && outcome.menu !== 'snooze';
}

/** A suggestion that is already dealt with on the server: a push for it has nothing left to run. */
export function isFinalStatus(status: CornerSuggestion['status']): boolean {
	return status === 'used' || status === 'dismissed' || status === 'skipped' || status === 'expired';
}

/**
 * Time left on the auto-close clock when it starts again after a pause: what was left when it
 * paused (or the full time), but never less than a few seconds so a card that comes back is not
 * gone the moment it reappears.
 */
export function resumeAutoCloseMs(remaining: number | null, full: number, floor = 5_000): number {
	if (remaining == null) return full;
	return Math.min(full, Math.max(remaining, floor));
}
